import { useState, useEffect, useCallback } from "react";

const API = "http://localhost:8001/api/bills/import-orders";
const CURRENCIES = ["USDINR", "EURINR", "GBPINR", "AEDINR", "SGDINR"];

const fmt = (n) =>
  n == null || n === "" || isNaN(Number(n)) ? "—" : Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });

const statusColor = (s) => {
  if (s === "OPEN")      return { bg: "#e2efda", color: "#375623" };
  if (s === "PART_PAID") return { bg: "#fff2cc", color: "#7d6608" };
  if (s === "CLOSED")    return { bg: "#deeaf1", color: "#1F4E79" };
  if (s === "CANCELLED") return { bg: "#fce4d6", color: "#c00" };
  return { bg: "#f5f5f5", color: "#333" };
};

const milestoneColor = (s) =>
  s === "PAID"
    ? { bg: "#e2efda", color: "#375623" }
    : { bg: "#fff2cc", color: "#7d6608" };

const safeStr = (v) => (v == null ? "" : String(v));

export default function ImportOrders({ token }) {
  const headers = { "Content-Type": "application/json", authorization: token };

  // ── State ────────────────────────────────────────────────────
  const [orders,        setOrders]        = useState([]);
  const [sellers,       setSellers]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [schedule,      setSchedule]      = useState([]);
  const [payments,      setPayments]      = useState([]);
  const [showForm,      setShowForm]      = useState(false);
  const [editOrder,     setEditOrder]     = useState(null);
  const [filterStatus,  setFilterStatus]  = useState("ALL");
  const [filterSeller,  setFilterSeller]  = useState("");
  const [msg,           setMsg]           = useState({ text: "", type: "ok" });

  // Payment form — triggered from milestone Mark Paid
  const emptyPay = {
    milestoneId:   null,
    milestoneName: "",
    paymentDate:   "",
    fcAmount:      "",
    spotRate:      "",
    notes:         ""
  };
  const [payForm,     setPayForm]     = useState(emptyPay);
  const [showPayForm, setShowPayForm] = useState(false);

  // Order form
  const emptyForm = {
    sellerCode: "", orderNo: "", orderDate: "", currency: "USDINR",
    fcAmount: "", costRate: "", expectedPaymentDate: "", bank: "", notes: ""
  };
  const [form, setForm] = useState(emptyForm);

  // Milestone rows in order form
  const emptyMilestone = { milestoneName: "", fcAmount: "", expectedDate: "" };
  const [milestones, setMilestones] = useState([{ ...emptyMilestone }]);

  // ── Loaders ──────────────────────────────────────────────────
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      let url = API + "?";
      if (filterStatus !== "ALL") url += `status=${filterStatus}&`;
      if (filterSeller)           url += `sellerCode=${filterSeller}&`;
      const res  = await fetch(url, { headers });
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch { showMsg("Error loading orders", "err"); }
    setLoading(false);
  }, [filterStatus, filterSeller, token]);

  const loadSellers = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/sellers`, { headers });
      const data = await res.json();
      setSellers(Array.isArray(data) ? data : []);
    } catch {}
  }, [token]);

  const loadSchedule = useCallback(async (id) => {
    try {
      const res  = await fetch(`${API}/${id}/schedule`, { headers });
      const data = await res.json();
      setSchedule(Array.isArray(data) ? data : []);
    } catch { setSchedule([]); }
  }, [token]);

  const loadPayments = useCallback(async (id) => {
    try {
      const res  = await fetch(`${API}/${id}/payments`, { headers });
      const data = await res.json();
      setPayments(Array.isArray(data) ? data : []);
    } catch { setPayments([]); }
  }, [token]);

  useEffect(() => { loadOrders(); loadSellers(); }, [loadOrders, loadSellers]);

  // ── Helpers ──────────────────────────────────────────────────
  const showMsg = (text, type = "ok") => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: "", type: "ok" }), 3500);
  };

  const refreshSelected = (id) => {
    loadSchedule(id);
    loadPayments(id);
    loadOrders();
  };

  // ── Milestone helpers ────────────────────────────────────────
  const addMilestone    = () => setMilestones([...milestones, { ...emptyMilestone }]);
  const removeMilestone = (i) => setMilestones(milestones.filter((_, idx) => idx !== i));
  const updateMilestone = (i, field, value) => {
    const u = [...milestones];
    u[i] = { ...u[i], [field]: value };
    setMilestones(u);
  };
  const totalScheduledFc = milestones.reduce((s, m) => s + (parseFloat(m.fcAmount) || 0), 0);

  // ── Open Mark Paid form ──────────────────────────────────────
  // Called when user clicks Mark Paid on a milestone row
  const openMarkPaid = (milestone) => {
    setPayForm({
      milestoneId:   milestone.scheduleId,
      milestoneName: safeStr(milestone.milestoneName),
      paymentDate:   "",
      fcAmount:      safeStr(milestone.fcAmount),
      spotRate:      "",
      notes:         ""
    });
    setShowPayForm(true);
  };

  // ── Save payment + mark milestone paid ───────────────────────
  const savePayment = async () => {
    if (!payForm.paymentDate)
      return showMsg("Payment date is required", "err");
    if (!payForm.fcAmount || parseFloat(payForm.fcAmount) <= 0)
      return showMsg("FC amount must be greater than zero", "err");
    if (!payForm.spotRate || parseFloat(payForm.spotRate) <= 0)
      return showMsg("Spot rate is required", "err");

    const fc       = parseFloat(payForm.fcAmount);
    const spotRate = parseFloat(payForm.spotRate);

    const payload = {
      paymentType: "ADVANCE",
      paymentDate: payForm.paymentDate,
      fcAmount:    fc,
      spotFc:      fc,
      spotRate:    spotRate,
      forwardId:   null,
      forwardFc:   null,
      notes:       payForm.notes || null
    };

    try {
      // Save payment
      const res = await fetch(
        `${API}/${selectedOrder.importOrderId}/payments`,
        { method: "POST", headers, body: JSON.stringify(payload) }
      );
      if (!res.ok) return showMsg(await res.text(), "err");

      // Mark milestone as paid
      await fetch(
        `${API}/${selectedOrder.importOrderId}/schedule/${payForm.milestoneId}/mark-paid`,
        { method: "PUT", headers }
      );

      showMsg("Payment saved ✅");
      setShowPayForm(false);
      setPayForm(emptyPay);
      refreshSelected(selectedOrder.importOrderId);
    } catch { showMsg("Error saving payment", "err"); }
  };

  // ── Select order ─────────────────────────────────────────────
  const selectOrder = (order) => {
    setSelectedOrder(order);
    setShowPayForm(false);
    loadSchedule(order.importOrderId);
    loadPayments(order.importOrderId);
  };

  // ── Open new order form ──────────────────────────────────────
  const openNew = () => {
    setForm(emptyForm);
    setMilestones([{ ...emptyMilestone }]);
    setEditOrder(null);
    setShowForm(true);
  };

  // ── Open edit order form ─────────────────────────────────────
  const openEdit = async (order) => {
    setForm({
      sellerCode:          safeStr(order.sellerCode),
      orderNo:             safeStr(order.orderNo),
      orderDate:           safeStr(order.orderDate).substring(0, 10),
      currency:            safeStr(order.currency) || "USDINR",
      fcAmount:            safeStr(order.fcAmount),
      costRate:            safeStr(order.costRate),
      expectedPaymentDate: safeStr(order.expectedPaymentDate).substring(0, 10),
      bank:                safeStr(order.bank),
      notes:               safeStr(order.notes)
    });
    setEditOrder(order);
    try {
      const res  = await fetch(`${API}/${order.importOrderId}/schedule`, { headers });
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        setMilestones(data.map(s => ({
          milestoneName: safeStr(s.milestoneName),
          fcAmount:      safeStr(s.fcAmount),
          expectedDate:  safeStr(s.expectedDate).substring(0, 10)
        })));
      } else {
        setMilestones([{ ...emptyMilestone }]);
      }
    } catch { setMilestones([{ ...emptyMilestone }]); }
    setShowForm(true);
  };

  // ── Save order ───────────────────────────────────────────────
  const saveOrder = async () => {
    if (!form.sellerCode || !form.orderNo || !form.orderDate ||
        !form.fcAmount   || !form.costRate)
      return showMsg("Please fill all required fields", "err");

    const payload = {
      sellerCode:          form.sellerCode,
      orderNo:             form.orderNo,
      orderDate:           form.orderDate,
      currency:            form.currency,
      fcAmount:            parseFloat(form.fcAmount),
      costRate:            parseFloat(form.costRate),
      expectedPaymentDate: form.expectedPaymentDate || null,
      bank:                form.bank  || null,
      notes:               form.notes || null
    };

    try {
      const url    = editOrder ? `${API}/${editOrder.importOrderId}` : API;
      const method = editOrder ? "PUT" : "POST";
      const res    = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (!res.ok) return showMsg(await res.text(), "err");
      const saved   = await res.json();
      const orderId = editOrder ? editOrder.importOrderId : saved.importOrderId;

      // Save milestones
      const valid = milestones.filter(m => m.milestoneName && m.fcAmount);
      if (valid.length > 0) {
        await fetch(`${API}/${orderId}/schedule`, {
          method: "POST", headers,
          body: JSON.stringify(valid.map(m => ({
            milestoneName: m.milestoneName,
            fcAmount:      parseFloat(m.fcAmount),
            expectedDate:  m.expectedDate || null
          })))
        });
      }

      showMsg(editOrder ? "Order updated ✅" : "Order saved ✅");
      setShowForm(false);
      loadOrders();
      loadSellers();
      if (selectedOrder?.importOrderId === orderId) loadSchedule(orderId);
    } catch { showMsg("Error saving order", "err"); }
  };

  // ── Cancel order ─────────────────────────────────────────────
  const cancelOrder = async (id) => {
    if (!window.confirm("Cancel this import order?")) return;
    try {
      const res = await fetch(`${API}/${id}/cancel`, { method: "PUT", headers });
      if (!res.ok) return showMsg(await res.text(), "err");
      showMsg("Order cancelled");
      if (selectedOrder?.importOrderId === id) setSelectedOrder(null);
      loadOrders();
    } catch { showMsg("Error cancelling", "err"); }
  };

  // ── Computed ─────────────────────────────────────────────────
  const inrAmount = payForm.fcAmount && payForm.spotRate
    ? parseFloat(payForm.fcAmount) * parseFloat(payForm.spotRate)
    : null;

  const costRate = selectedOrder ? Number(selectedOrder.costRate) || 0 : 0;

  // ── Styles ───────────────────────────────────────────────────
  const inp = {
    width: "100%", padding: "7px 10px", borderRadius: "6px",
    border: "1px solid #CBD5E1", fontSize: "13px",
    boxSizing: "border-box", fontFamily: "inherit"
  };
  const lbl   = { fontSize: "12px", color: "#555", marginBottom: "3px", display: "block", fontWeight: "500" };
  const btn   = (bg, fg = "#fff") => ({
    padding: "7px 16px", borderRadius: "6px", border: "none",
    background: bg, color: fg, cursor: "pointer", fontSize: "13px", fontWeight: "500"
  });
  const smBtn = (bg, fg = "#fff") => ({ ...btn(bg, fg), padding: "4px 10px", fontSize: "11px" });

  // ── Render ───────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "Arial, sans-serif", fontSize: "13px" }}>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "12px", marginBottom: "16px" }}>
        {[
          { label: "Total",     value: orders.length,                                        color: "#1F4E79" },
          { label: "Open",      value: orders.filter(o => o.status === "OPEN").length,      color: "#375623" },
          { label: "Part Paid", value: orders.filter(o => o.status === "PART_PAID").length, color: "#7d6608" },
          { label: "Closed",    value: orders.filter(o => o.status === "CLOSED").length,    color: "#1F4E79" },
        ].map(c => (
          <div key={c.label} style={{ background: "#f8fafc", border: "1px solid #e2e8f0",
            borderRadius: "8px", padding: "14px 16px" }}>
            <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "4px" }}>{c.label}</div>
            <div style={{ fontSize: "22px", fontWeight: "700", color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: "12px" }}>
        <div style={{ fontSize: "15px", fontWeight: "600", color: "#1F4E79" }}>📥 Import Orders</div>
        <button onClick={openNew} style={btn("#1F4E79")}>+ New Import Order</button>
      </div>

      {/* Message */}
      {msg.text && (
        <div style={{
          background:   msg.type === "err" ? "#fce4d6" : "#e2efda",
          border:       `1px solid ${msg.type === "err" ? "#c00" : "#70ad47"}`,
          borderRadius: "6px", padding: "8px 14px", marginBottom: "12px"
        }}>{msg.text}</div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "14px",
        flexWrap: "wrap", alignItems: "center" }}>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ ...inp, width: "140px" }}>
          <option value="ALL">All Status</option>
          <option value="OPEN">Open</option>
          <option value="PART_PAID">Part Paid</option>
          <option value="CLOSED">Closed</option>
        </select>
        <select value={filterSeller} onChange={e => setFilterSeller(e.target.value)}
          style={{ ...inp, width: "160px" }}>
          <option value="">All Sellers</option>
          {sellers.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={loadOrders} style={btn("#64748b")}>🔄 Refresh</button>
        <span style={{ fontSize: "12px", color: "#64748b", marginLeft: "auto" }}>
          {orders.length} orders
        </span>
      </div>

      {/* Orders table */}
      <div style={{ overflowX: "auto", marginBottom: "20px",
        border: "1px solid #e2e8f0", borderRadius: "8px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
          <thead>
            <tr style={{ background: "#1F4E79", color: "#fff" }}>
              {["Order No","Seller","Date","Currency","FC Amount","Cost Rate",
                "Exp. Payment","FC Paid","FC Balance","Bank","Status","Actions"].map(h => (
                <th key={h} style={{ padding: "9px 10px", textAlign: "left",
                  whiteSpace: "nowrap", fontWeight: "500", fontSize: "11px" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} style={{ padding: "24px", textAlign: "center", color: "#888" }}>
                Loading...
              </td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={12} style={{ padding: "24px", textAlign: "center", color: "#888" }}>
                No import orders found
              </td></tr>
            ) : orders.map((o, i) => {
              const sc        = statusColor(o.status);
              const isSelected = selectedOrder?.importOrderId === o.importOrderId;
              const fcPaid    = Number(o.fcPaid)    || 0;
              const fcBalance = Number(o.fcBalance) || 0;
              return (
                <tr key={o.importOrderId}
                  style={{ background: isSelected ? "#deeaf1" : i % 2 === 0 ? "#fff" : "#f9fafb",
                    borderBottom: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "7px 10px", fontWeight: "500" }}>{safeStr(o.orderNo)}</td>
                  <td style={{ padding: "7px 10px" }}><strong>{safeStr(o.sellerCode)}</strong></td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{safeStr(o.orderDate).substring(0,10)}</td>
                  <td style={{ padding: "7px 10px" }}>{safeStr(o.currency)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmt(o.fcAmount)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmt(o.costRate)}</td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                    {safeStr(o.expectedPaymentDate).substring(0,10) || "—"}
                  </td>
                  <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmt(fcPaid)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right",
                    color: fcBalance > 0 ? "#c00" : "#375623", fontWeight: "600" }}>
                    {fmt(fcBalance)}
                  </td>
                  <td style={{ padding: "7px 10px" }}>{safeStr(o.bank) || "—"}</td>
                  <td style={{ padding: "7px 10px" }}>
                    <span style={{ background: sc.bg, color: sc.color,
                      borderRadius: "99px", padding: "2px 9px",
                      fontSize: "11px", fontWeight: "600" }}>
                      {o.status}
                    </span>
                  </td>
                  <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                    <button onClick={() => selectOrder(o)}
                      style={{ ...smBtn("#1F4E79"), marginRight: "4px" }}>
                      {isSelected ? "▲ Hide" : "▼ Details"}
                    </button>
                    {o.status === "OPEN" && (
                      <>
                        <button onClick={e => { e.stopPropagation(); openEdit(o); }}
                          style={{ ...smBtn("#555"), marginRight: "4px" }}>Edit</button>
                        <button onClick={e => { e.stopPropagation(); cancelOrder(o.importOrderId); }}
                          style={smBtn("#c00")}>Cancel</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Detail panel — shown below selected order */}
      {selectedOrder && (
        <div style={{ border: "1px solid #bcd4e6", borderRadius: "8px",
          padding: "16px", marginBottom: "20px", background: "#f0f7ff" }}>

          {/* Panel header */}
          <div style={{ display: "flex", justifyContent: "space-between",
            alignItems: "center", marginBottom: "14px" }}>
            <div>
              <span style={{ fontSize: "14px", fontWeight: "600", color: "#1F4E79" }}>
                {selectedOrder.orderNo} — {selectedOrder.sellerCode}
              </span>
              <span style={{ marginLeft: "12px", fontSize: "12px", color: "#555" }}>
                {selectedOrder.currency} &nbsp;|&nbsp;
                Total: <strong>{fmt(Number(selectedOrder.fcAmount))}</strong> &nbsp;|&nbsp;
                Paid: <strong style={{ color: "#375623" }}>{fmt(Number(selectedOrder.fcPaid))}</strong> &nbsp;|&nbsp;
                Balance: <strong style={{ color: "#c00" }}>{fmt(Number(selectedOrder.fcBalance))}</strong> &nbsp;|&nbsp;
                Cost Rate: <strong>{fmt(Number(selectedOrder.costRate))}</strong>
              </span>
            </div>
            <button onClick={() => { setSelectedOrder(null); setShowPayForm(false); }}
              style={smBtn("#888")}>✕ Close</button>
          </div>

          {/* Payment Schedule */}
          {schedule.length > 0 ? (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "12px", fontWeight: "600", color: "#1F4E79",
                marginBottom: "8px" }}>📅 Payment Schedule</div>
              <table style={{ width: "100%", borderCollapse: "collapse",
                fontSize: "12px", background: "#fff", borderRadius: "6px", overflow: "hidden" }}>
                <thead>
                  <tr style={{ background: "#1F4E79", color: "#fff" }}>
                    {["#","Milestone","FC Amount","Expected Date","Status","Action"].map(h => (
                      <th key={h} style={{ padding: "7px 10px", textAlign: "left",
                        fontWeight: "500", fontSize: "11px" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((s, i) => {
                    const mc = milestoneColor(s.status);
                    return (
                      <tr key={s.scheduleId}
                        style={{ borderBottom: "1px solid #eee",
                          background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                        <td style={{ padding: "7px 10px", color: "#888" }}>{i + 1}</td>
                        <td style={{ padding: "7px 10px", fontWeight: "500" }}>{safeStr(s.milestoneName)}</td>
                        <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmt(s.fcAmount)}</td>
                        <td style={{ padding: "7px 10px" }}>{safeStr(s.expectedDate).substring(0,10) || "—"}</td>
                        <td style={{ padding: "7px 10px" }}>
                          <span style={{ background: mc.bg, color: mc.color,
                            borderRadius: "99px", padding: "2px 9px",
                            fontSize: "11px", fontWeight: "600" }}>
                            {s.status}
                          </span>
                        </td>
                        <td style={{ padding: "7px 10px" }}>
                          {s.status === "PENDING" &&
                           selectedOrder.status !== "CANCELLED" && (
                            <button
                              onClick={() => openMarkPaid(s)}
                              style={smBtn("#1F4E79")}>
                              💳 Make Payment
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ background: "#f0f4f8", fontWeight: "600" }}>
                    <td colSpan={2} style={{ padding: "7px 10px" }}>Total Scheduled</td>
                    <td style={{ padding: "7px 10px", textAlign: "right" }}>
                      {fmt(schedule.reduce((s, m) => s + Number(m.fcAmount), 0))}
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ fontSize: "12px", color: "#888", marginBottom: "14px",
              padding: "10px", background: "#fff", borderRadius: "6px",
              border: "1px solid #e2e8f0" }}>
              No payment schedule defined for this order.
            </div>
          )}

          {/* Payment form — triggered by Make Payment button */}
          {showPayForm && (
            <div style={{ background: "#fff", border: "2px solid #1F4E79",
              borderRadius: "8px", padding: "16px", marginBottom: "16px" }}>
              <div style={{ fontSize: "13px", fontWeight: "600", color: "#1F4E79",
                marginBottom: "4px" }}>💳 Payment Entry</div>
              <div style={{ fontSize: "12px", color: "#555", marginBottom: "12px" }}>
                Milestone: <strong>{payForm.milestoneName}</strong>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "12px" }}>
                <div>
                  <label style={lbl}>Payment Date *</label>
                  <input type="date" value={payForm.paymentDate}
                    onChange={e => setPayForm({ ...payForm, paymentDate: e.target.value })}
                    style={inp} />
                </div>
                <div>
                  <label style={lbl}>FC Amount *</label>
                  <input type="number" value={payForm.fcAmount}
                    onChange={e => setPayForm({ ...payForm, fcAmount: e.target.value })}
                    style={inp} placeholder="0.00" />
                </div>
                <div>
                  <label style={lbl}>Spot Rate *</label>
                  <input type="number" value={payForm.spotRate}
                    onChange={e => setPayForm({ ...payForm, spotRate: e.target.value })}
                    style={inp} placeholder="0.00" />
                </div>
                <div>
                  <label style={lbl}>INR Amount (auto)</label>
                  <input readOnly
                    value={inrAmount != null ? fmt(inrAmount) : "—"}
                    style={{ ...inp, background: "#f0f4f8", color: "#1F4E79",
                      fontWeight: "600", cursor: "default" }} />
                </div>
                <div>
                  <label style={lbl}>Exch Diff (auto)</label>
                  <input readOnly
                    value={payForm.fcAmount && payForm.spotRate
                      ? fmt((costRate - parseFloat(payForm.spotRate)) * parseFloat(payForm.fcAmount))
                      : "—"}
                    style={{ ...inp, cursor: "default",
                      background: "#f0f4f8", fontWeight: "600",
                      color: payForm.spotRate && costRate - parseFloat(payForm.spotRate) >= 0
                        ? "#375623" : "#c00" }} />
                </div>
                <div>
                  <label style={lbl}>Notes</label>
                  <input type="text" placeholder="Optional" value={payForm.notes}
                    onChange={e => setPayForm({ ...payForm, notes: e.target.value })}
                    style={inp} />
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                <button onClick={savePayment} style={btn("#1F4E79")}>Save Payment ✅</button>
                <button onClick={() => setShowPayForm(false)} style={btn("#888")}>Cancel</button>
              </div>
            </div>
          )}

          {/* Payments Made table */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#1F4E79",
              marginBottom: "8px" }}>💳 Payments Made</div>
            <table style={{ width: "100%", borderCollapse: "collapse",
              fontSize: "12px", background: "#fff", borderRadius: "6px", overflow: "hidden" }}>
              <thead>
                <tr style={{ background: "#1F4E79", color: "#fff" }}>
                  {["#","Date","FC Amount","Spot Rate","INR Amount","Exch Diff","Notes"].map(h => (
                    <th key={h} style={{ padding: "7px 10px", textAlign: "left",
                      whiteSpace: "nowrap", fontWeight: "500", fontSize: "11px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: "16px", textAlign: "center", color: "#888" }}>
                    No payments recorded yet
                  </td></tr>
                ) : payments.map((p, i) => {
                  const fc       = Number(p.fcAmount) || 0;
                  const sr       = Number(p.spotRate)  || 0;
                  const inrAmt   = fc * sr;
                  const exchDiff = (costRate - sr) * fc;
                  return (
                    <tr key={p.paymentId}
                      style={{ borderBottom: "1px solid #eee",
                        background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                      <td style={{ padding: "7px 10px", color: "#888" }}>{p.paymentNo}</td>
                      <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{safeStr(p.paymentDate).substring(0,10)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: "600" }}>{fmt(fc)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmt(sr)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmt(inrAmt)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right",
                        color: exchDiff >= 0 ? "#375623" : "#c00", fontWeight: "600" }}>
                        {fmt(exchDiff)}
                      </td>
                      <td style={{ padding: "7px 10px" }}>{safeStr(p.notes) || "—"}</td>
                    </tr>
                  );
                })}
                {payments.length > 0 && (() => {
                  const totalFc      = payments.reduce((s, p) => s + (Number(p.fcAmount) || 0), 0);
                  const totalInr     = payments.reduce((s, p) => s + (Number(p.fcAmount) || 0) * (Number(p.spotRate) || 0), 0);
                  const totalExch    = payments.reduce((s, p) => s + (costRate - (Number(p.spotRate) || 0)) * (Number(p.fcAmount) || 0), 0);
                  return (
                    <tr style={{ background: "#f0f4f8", fontWeight: "600" }}>
                      <td colSpan={2} style={{ padding: "7px 10px" }}>Total</td>
                      <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmt(totalFc)}</td>
                      <td style={{ padding: "7px 10px" }}></td>
                      <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmt(totalInr)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "right",
                        color: totalExch >= 0 ? "#375623" : "#c00" }}>{fmt(totalExch)}</td>
                      <td></td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Order form modal */}
      {showForm && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: "10px", padding: "24px",
            width: "680px", maxHeight: "90vh", overflowY: "auto" }}>

            <h3 style={{ margin: "0 0 16px", fontSize: "15px", color: "#1F4E79" }}>
              {editOrder ? "✏️ Edit Import Order" : "📥 New Import Order"}
            </h3>

            {/* Order fields */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: "12px", marginBottom: "14px" }}>
              <div>
                <label style={lbl}>Seller Code *</label>
                <input list="sellers-list" value={form.sellerCode}
                  onChange={e => setForm({ ...form, sellerCode: e.target.value.toUpperCase() })}
                  style={inp} placeholder="Seller name / code" />
                <datalist id="sellers-list">
                  {sellers.map(s => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div>
                <label style={lbl}>Order No *</label>
                <input value={form.orderNo}
                  onChange={e => setForm({ ...form, orderNo: e.target.value })}
                  style={inp} placeholder="IMP-2026-001" />
              </div>
              <div>
                <label style={lbl}>Order Date *</label>
                <input type="date" value={form.orderDate}
                  onChange={e => setForm({ ...form, orderDate: e.target.value })}
                  style={inp} />
              </div>
              <div>
                <label style={lbl}>Currency *</label>
                <select value={form.currency}
                  onChange={e => setForm({ ...form, currency: e.target.value })} style={inp}>
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>FC Amount *</label>
                <input type="number" value={form.fcAmount}
                  onChange={e => setForm({ ...form, fcAmount: e.target.value })}
                  style={inp} placeholder="0.00" />
              </div>
              <div>
                <label style={lbl}>Cost Rate *</label>
                <input type="number" value={form.costRate}
                  onChange={e => setForm({ ...form, costRate: e.target.value })}
                  style={inp} placeholder="0.00" />
              </div>
              <div>
                <label style={lbl}>Expected Payment Date</label>
                <input type="date" value={form.expectedPaymentDate}
                  onChange={e => setForm({ ...form, expectedPaymentDate: e.target.value })}
                  style={inp} />
              </div>
              <div>
                <label style={lbl}>Bank</label>
                <input value={form.bank}
                  onChange={e => setForm({ ...form, bank: e.target.value })}
                  style={inp} placeholder="Bank name" />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lbl}>Notes</label>
                <input value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  style={inp} placeholder="Optional" />
              </div>
            </div>

            {/* Payment schedule */}
            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "14px", marginBottom: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                alignItems: "center", marginBottom: "10px" }}>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#1F4E79" }}>
                  📅 Payment Schedule
                  <span style={{ fontWeight: "400", color: "#888", fontSize: "12px",
                    marginLeft: "6px" }}>(Optional)</span>
                </div>
                <button onClick={addMilestone} style={smBtn("#1F4E79")}>+ Add Milestone</button>
              </div>

              {milestones.map((m, i) => (
                <div key={i} style={{ display: "grid",
                  gridTemplateColumns: "2fr 1.5fr 1.5fr auto",
                  gap: "8px", marginBottom: "8px", alignItems: "end" }}>
                  <div>
                    {i === 0 && <label style={lbl}>Milestone</label>}
                    <input value={m.milestoneName}
                      onChange={e => updateMilestone(i, "milestoneName", e.target.value)}
                      style={inp} placeholder="e.g. Advance at Order" />
                  </div>
                  <div>
                    {i === 0 && <label style={lbl}>FC Amount</label>}
                    <input type="number" value={m.fcAmount}
                      onChange={e => updateMilestone(i, "fcAmount", e.target.value)}
                      style={inp} placeholder="0.00" />
                  </div>
                  <div>
                    {i === 0 && <label style={lbl}>Expected Date</label>}
                    <input type="date" value={m.expectedDate}
                      onChange={e => updateMilestone(i, "expectedDate", e.target.value)}
                      style={inp} />
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    {milestones.length > 1 && (
                      <button onClick={() => removeMilestone(i)}
                        style={{ ...smBtn("#c00"), padding: "7px 10px",
                          marginTop: i === 0 ? "18px" : "0" }}>✕</button>
                    )}
                  </div>
                </div>
              ))}

              {milestones.some(m => m.fcAmount) && (
                <div style={{ textAlign: "right", fontSize: "12px",
                  color: "#555", marginTop: "6px" }}>
                  Total Scheduled: <strong>{fmt(totalScheduledFc)}</strong>
                  {form.fcAmount && Math.abs(totalScheduledFc - parseFloat(form.fcAmount)) > 0.01 && (
                    <span style={{ color: "#c00", marginLeft: "8px" }}>
                      ⚠ Order FC is {fmt(parseFloat(form.fcAmount))}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={saveOrder} style={btn("#1F4E79")}>
                {editOrder ? "Update Order" : "Save Order"}
              </button>
              <button onClick={() => setShowForm(false)} style={btn("#888")}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}